/**
 * Offline billing support: cached catalog + a client-side mirror of the server discount engine so
 * the counter can keep billing (and printing) when the internet drops. Bills are queued locally
 * with a temporary OFF-n number and replaced by the real invoice number once synced.
 */
const CACHE_KEY = "bds_offline_cache";
export const QUEUE_KEY = "bds_offline_queue";
const SEQ_KEY = "bds_offline_seq";

const read = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

export const readQueue = () => read(QUEUE_KEY, []);
export const writeQueue = (q) => localStorage.setItem(QUEUE_KEY, JSON.stringify(q));

export const saveCache = ({ products, categories, settings, rules }) =>
  localStorage.setItem(
    CACHE_KEY,
    JSON.stringify({ products, categories, settings, rules: rules || [], cached_at: new Date().toISOString() })
  );

export const loadCache = () => read(CACHE_KEY, null);

export const nextOfflineNo = () => {
  const n = Number(localStorage.getItem(SEQ_KEY) || 0) + 1;
  localStorage.setItem(SEQ_KEY, String(n));
  return `OFF-${n}`;
};

const r2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

const isEligible = (product, rule) => {
  if (!rule?.enabled) return false;
  const today = new Date().toISOString().slice(0, 10);
  if (rule.start_date && today < rule.start_date) return false;
  if (rule.end_date && today > rule.end_date) return false;
  if ((rule.eligible_products || []).includes(product.product_id)) return true;
  return (rule.eligible_categories || []).includes(product.category);
};

const slabPercent = (eligibleSubtotal, rule) => {
  let best = 0;
  (rule?.slabs || []).forEach((s) => {
    const min = Number(s.min || 0);
    const max = s.max === null || s.max === undefined ? null : Number(s.max);
    if (eligibleSubtotal >= min && (max === null || eligibleSubtotal <= max)) best = Number(s.percent || 0);
  });
  return best;
};

/** Mirrors the server automatic rule engine (best matching rule wins). */
const ruleMatches = (rule, product, qty, lineAmount, gross) => {
  if (rule.active === false) return false;
  const today = new Date().toISOString().slice(0, 10);
  if (rule.start_date && today < rule.start_date) return false;
  if (rule.end_date && today > rule.end_date) return false;
  if (Number(qty) < Number(rule.min_quantity || 0)) return false;
  if (lineAmount < Number(rule.min_line_value || 0)) return false;
  if (gross < Number(rule.min_bill_value || 0)) return false;
  const targets = (rule.targets || []).map((t) => String(t).trim().toLowerCase()).filter(Boolean);
  const scope = rule.scope || "bill";
  if (scope === "product") {
    const keys = [product.product_id, product.stk_code, product.sku, product.name, product.id]
      .filter(Boolean)
      .map((v) => String(v).trim().toLowerCase());
    return targets.some((t) => keys.includes(t));
  }
  if (!product.discount_eligible) return false;
  if (scope === "category") {
    const cat = String(product.category || "").trim().toLowerCase();
    const sub = String(product.subcategory || "").trim().toLowerCase();
    return targets.includes(cat) || targets.includes(sub);
  }
  return true;
};

const ruleAmount = (rule, lineAmount, qty) => {
  const value = Number(rule.value || 0);
  if (value <= 0) return 0;
  let out;
  if (rule.discount_type === "percent") {
    out = r2((lineAmount * value) / 100);
    const cap = Number(rule.max_discount_amount || 0);
    if (cap > 0 && out > cap) out = cap;
  } else if (rule.fixed_basis === "per_unit") {
    out = r2(value * Number(qty));
  } else out = value;
  return Math.min(out, lineAmount);
};

/** Mirrors backend compute_bill() closely enough for an offline receipt. */
export const computeBillLocal = (cart, settings, manualDiscount = 0, rules = []) => {
  const rule = settings?.discount || {};
  const tax = settings?.tax || {};
  const inv = settings?.invoice || {};

  let gross = 0;
  const lines = cart.map((c) => {
    const rate = Number(c.product.selling_price || 0);
    const amount = r2(Number(c.qty) * rate);
    gross = r2(gross + amount);
    return { c, rate, amount, legacy: isEligible(c.product, rule) };
  });

  const percent = slabPercent(
    lines.filter((l) => l.legacy).reduce((s, l) => r2(s + l.amount), 0),
    rule
  );

  let eligibleSub = 0;
  let autoDiscount = 0;
  const appliedRules = [];
  lines.forEach((l) => {
    const slab = l.legacy && percent > 0 ? r2((l.amount * percent) / 100) : 0;
    let best = 0;
    let bestName = "";
    (rules || []).forEach((r) => {
      if (!ruleMatches(r, l.c.product, l.c.qty, l.amount, gross)) return;
      const amt = ruleAmount(r, l.amount, l.c.qty);
      if (amt > best) {
        best = amt;
        bestName = r.name || "";
      }
    });
    if (best >= slab && best > 0) {
      l.auto = best;
      l.rule = bestName;
    } else {
      l.auto = slab;
      l.rule = slab > 0 ? rule.campaign || "Automatic Discount" : "";
    }
    l.eligible = l.auto > 0 || l.legacy || !!l.c.product.discount_eligible;
    if (l.eligible) eligibleSub = r2(eligibleSub + l.amount);
    autoDiscount = r2(autoDiscount + l.auto);
    if (l.auto > 0 && l.rule && !appliedRules.includes(l.rule)) appliedRules.push(l.rule);
  });

  const productNet = r2(gross - autoDiscount);
  const manual = Math.min(r2(manualDiscount), Math.max(0, productNet));
  const totalDiscount = r2(autoDiscount + manual);

  let allocated = 0;
  const items = lines.map(({ c, rate, amount, eligible, auto, rule: ruleName }) => {
    let disc = auto;
    let lineManual = 0;
    if (manual > 0 && gross > 0) {
      lineManual = r2((manual * amount) / gross);
      disc = r2(disc + lineManual);
    }
    allocated = r2(allocated + disc);
    const net = r2(amount - disc);
    const gstRate = tax.gst_enabled ? Number(c.product.gst_rate || 0) : 0;
    const taxAmt = gstRate
      ? tax.tax_inclusive === false
        ? r2((net * gstRate) / 100)
        : r2(net - net / (1 + gstRate / 100))
      : 0;
    return {
      product_oid: c.id,
      product_id: c.product.product_id,
      product_name: c.product.name,
      sku: c.product.sku || "",
      stk_code: c.product.stk_code || "",
      category: c.product.category || "",
      unit: c.product.unit || "PCS",
      qty: Number(c.qty),
      rate,
      mrp: Number(c.product.mrp || 0),
      amount,
      discount_eligible: eligible,
      discount: disc,
      auto_discount: auto,
      manual_discount: lineManual,
      discount_rule: ruleName,
      gst_rate: gstRate,
      tax: taxAmt,
      net_amount: net,
      line_total: tax.tax_inclusive === false ? r2(net + taxAmt) : net,
      returned_qty: 0,
    };
  });

  const drift = r2(totalDiscount - allocated);
  if (items.length && Math.abs(drift) >= 0.01) {
    items[items.length - 1].discount = r2(items[items.length - 1].discount + drift);
    items[items.length - 1].net_amount = r2(items[items.length - 1].amount - items[items.length - 1].discount);
  }

  const taxTotal = r2(items.reduce((s, i) => s + i.tax, 0));
  const afterDiscount = r2(gross - totalDiscount);
  const preRound = tax.gst_enabled && tax.tax_inclusive === false ? r2(afterDiscount + taxTotal) : afterDiscount;
  const grand = inv.round_off === false ? preRound : Math.round(preRound);

  return {
    items,
    subtotal: gross,
    eligible_subtotal: eligibleSub,
    non_discountable_subtotal: r2(gross - eligibleSub),
    discount_percent: percent,
    auto_discount: autoDiscount,
    auto_discount_rules: appliedRules,
    manual_discount: manual,
    product_net: productNet,
    discount_amount: totalDiscount,
    amount_after_discount: afterDiscount,
    tax_amount: taxTotal,
    round_off: r2(grand - preRound),
    grand_total: r2(grand),
    net_sales: afterDiscount,
    offline: true,
  };
};

/** Builds a printable invoice object for an offline bill. */
export const buildOfflineInvoice = ({ bill, payload, offlineNo, cashier }) => {
  const now = new Date();
  const paid = Number(payload.amount_paid || 0);
  const due = Math.max(0, r2(bill.grand_total - paid));
  return {
    ...bill,
    invoice_no: offlineNo,
    offline: true,
    offline_pending: true,
    transaction_id: payload.client_txn_id,
    client_txn_id: payload.client_txn_id,
    date: now.toISOString().slice(0, 10),
    time: now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }),
    customer_name: payload.customer_name || "Walk-in Customer",
    customer_mobile: payload.customer_mobile || "",
    customer_whatsapp: payload.customer_whatsapp || "",
    payment_method: payload.payment_method,
    payments: payload.payments || [],
    amount_paid: paid,
    change_due: Math.max(0, r2(paid - bill.grand_total)),
    due_amount: due,
    payment_status: due > 0 ? (paid > 0 ? "PARTIAL" : "UNPAID") : "PAID",
    total_qty: bill.items.reduce((s, i) => s + Number(i.qty), 0),
    item_count: bill.items.length,
    cashier: cashier || "Shop Keeper",
    status: "COMPLETED",
    notes: payload.notes || "",
  };
};
